<template>
  <div
    class="rounded-lg w-full overflow-hidden lg:max-w-[186px]"
    :class="{
      'bg-surface-brand-min': !consentGiven,
      'bg-static-default-hi mood-inverse lg:bg-surface-brand-min lg:mood-main':
        consentGiven,
    }"
    data-test="app-store-download-banner-small"
  >
    <div
      class="flex"
      :class="{
        'h-full lg:justify-between': !consentGiven,
        'flex-col-reverse justify-center': consentGiven,
      }"
    >
      <RevIllustration
        v-if="consentGiven && qrCodePath"
        :alt="i18n(translations.downloadAppQrCodeImage)"
        class="hidden w-full p-12 lg:block"
        data-test="app-store-download-banner-small-qr-code"
        :height="162"
        :src="qrCodePath"
        :width="162"
      />

      <div
        class="relative flex w-full"
        :class="{ 'items-center lg:items-start': consentGiven }"
      >
        <div :class="{ 'mr-36 w-full': consentGiven && onTableOrMobile }">
          <component
            :is="consentGiven && onTableOrMobile ? RevButtonBase : 'div'"
            class="flex w-full py-16 pl-16 lg:mr-0 lg:px-12 lg:pb-0 lg:pt-12"
            :class="{
              'justify-center lg:items-start': consentGiven,
            }"
            :target="consentGiven && onTableOrMobile ? '_blank' : undefined"
            :to="consentGiven && onTableOrMobile ? mobileTrackedUrl : undefined"
            @click="onAppDownloadClick"
          >
            <RevIllustration
              :alt="i18n(translations.downloadAppBrandImage)"
              class="self-center lg:hidden"
              :class="{
                'mr-16 !size-24': consentGiven,
                'mr-12 !size-40': !consentGiven,
              }"
              :height="40"
              :src="
                consentGiven
                  ? '/img/lead-collection/appDownload/bm-logo-minimal.svg'
                  : '/img/lead-collection/appDownload/bm-logo-dot-black.svg'
              "
              :width="40"
            />

            <span class="flex flex-col">
              <span
                v-if="title"
                class="heading-3 mr-24 hidden text-left lg:block"
                :class="{ 'text-static-default-hi': consentGiven }"
              >
                {{ i18n(title) }}
              </span>

              <span
                v-if="mobileTitle"
                class="body-1 mr-48 text-left lg:hidden"
                :class="{ 'text-static-default-hi': consentGiven }"
              >
                {{ i18n(mobileTitle) }}
              </span>

              <span v-if="subtitle" class="body-2 hidden lg:block">
                {{ i18n(subtitle) }}
              </span>

              <RevRating
                v-if="rating"
                class="mt-4 lg:mt-8"
                :score="rating"
                size="extra-small"
              />
            </span>
          </component>
        </div>

        <RevDivider
          class="border-static-default-low absolute right-36 h-full lg:hidden"
          :class="{ hidden: !consentGiven }"
          variant="vertical"
        />
      </div>
    </div>

    <div
      v-if="!consentGiven"
      class="flex justify-center px-12 pb-12 pt-0 lg:items-start lg:justify-start lg:pt-16"
    >
      <AppStoreButtons
        :apple-store-tracking-name="`${routeName}_new_app_store`"
        :apple-store-url="desktopTrackedUrl"
        class="hidden lg:flex lg:flex-col-reverse"
        :play-store-tracking-name="`${routeName}_new_google_play`"
        :play-store-url="desktopTrackedUrl"
        :show-in-all-countries="true"
        trackingZone="app_download_new_notification"
        @click="emit('actioned')"
      />

      <AppStoreButtons
        :apple-store-tracking-name="`${routeName}_new_app_store`"
        :apple-store-url="mobileTrackedUrl"
        class="flex lg:hidden lg:flex-col-reverse"
        :play-store-tracking-name="`${routeName}_new_google_play`"
        :play-store-url="mobileTrackedUrl"
        :show-in-all-countries="true"
        trackingZone="app_download_new_notification"
        @click="emit('actioned')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed } from 'vue'

import AppStoreButtons from '@backmarket/nuxt-layer-base/components/AppStoreButtons/AppStoreButtons.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevDivider } from '@ds/components/Divider'
import { RevIllustration } from '@ds/components/Illustration'
import { RevRating } from '@ds/components/Rating'

import translations from '../AppStoreDownloadBanner.translations'
import type { BannerVariantProps } from '../AppStoreDownloadBanner.types'

const i18n = useI18n()
const route = useRoute()
const breakpoint = useClientOnlyComputedBreakpoint()
const { trackClick } = useTracking()
const emit = defineEmits<{ actioned: [value: void] }>()

const {
  consentGiven = false,
  desktopTrackedUrl = '',
  mobileTrackedUrl = '',
} = defineProps<BannerVariantProps>()

const onTableOrMobile = computed(() => {
  return breakpoint.value < Breakpoint.LG
})

const routeName = computed(() => route.name?.toString() || '')

async function onAppDownloadClick() {
  if (!consentGiven || !onTableOrMobile.value || !mobileTrackedUrl) return

  trackClick({
    name: `${routeName.value}_new_tracked_store`,
    url: mobileTrackedUrl,
    zone: 'app_download_new_notification',
  })

  emit('actioned')
}
</script>
